import './afiliacionFlow.css';
import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
    fetchAfiliaciones,
    Afiliacion,
} from '../../../services/afiliadosService';
import GestionAfiliacionModal from './GestionAfiliacionModal';
import NuevaAfiliacionModal from './NuevaAfiliacionModal';
import PagoAfiliacionModal from './PagoAfiliacionModal';
import {
    Building2,
    Search,
    Plus,
    CheckCircle2,
    Clock,
    Gift,
    Shield,
    Sparkles,
    ChevronRight,
    Filter,
    Users,
    Activity,
    ExternalLink,
    AlertCircle,
    Calendar,
    Banknote,
} from 'lucide-react';

const AfiliadosPage: React.FC = () => {
    const [afiliaciones, setAfiliaciones] = useState<Afiliacion[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [searchTerm, setSearchTerm] = useState('');
    const [filterEstado, setFilterEstado] = useState<string>('todos');

    // Modales
    const [afiliacionSeleccionada, setAfiliacionSeleccionada] = useState<Afiliacion | null>(null);
    const [afiliacionParaPago, setAfiliacionParaPago] = useState<Afiliacion | null>(null);
    const [modalNuevaAbierto, setModalNuevaAbierto] = useState(false);

    const loadData = async () => {
        setLoading(true);
        try {
            const data = await fetchAfiliaciones();
            setAfiliaciones(data);
        } catch (err: any) {
            console.error('Error cargando afiliaciones:', err);
            setError('No se pudieron cargar las afiliaciones.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const safeAfiliaciones = useMemo(() => {
        if (!Array.isArray(afiliaciones)) return [];
        // Permitir ver domiciliaciones para cualquier rol de usuario
        return afiliaciones.filter(Boolean);
    }, [afiliaciones]);

    const filteredList = useMemo(() => {
        return safeAfiliaciones.filter((item) => {
            if (!item) return false;
            const term = searchTerm.toLowerCase();
            const matchQuery =
                item.empresa_nombre?.toLowerCase().includes(term) ||
                item.nit?.toLowerCase().includes(term) ||
                item.cliente_nombre?.toLowerCase().includes(term) ||
                item.representante_legal?.toLowerCase().includes(term);

            const matchEstado =
                filterEstado === 'todos' || item.estado === filterEstado;

            return matchQuery && matchEstado;
        });
    }, [safeAfiliaciones, searchTerm, filterEstado]);

    const stats = useMemo(() => {
        const total = safeAfiliaciones.length;
        const activos = safeAfiliaciones.filter((a) => a && a.estado === 'activo').length;
        const enProceso = safeAfiliaciones.filter((a) => a && a.estado === 'en_proceso').length;
        const totalHorasDisponibles = safeAfiliaciones.reduce(
            (acc, curr) => acc + Number(curr?.horas_gratuitas_disponibles || 0),
            0
        );
        return { total, activos, enProceso, totalHorasDisponibles };
    }, [safeAfiliaciones]);

    return (
        <div className="p-6 max-w-7xl mx-auto space-y-6 animate-fadeIn">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-3xl shadow-sm border border-gray-100">
                <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-gradient-to-tr from-[#c1348b] to-[#8f1f64] text-white rounded-2xl flex items-center justify-center shadow-md">
                        <Building2 className="w-7 h-7" />
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <h1 className="text-2xl font-black text-gray-900 tracking-tight">Domiciliados (Oficina Virtual)</h1>
                            <span className="px-2.5 py-0.5 bg-purple-100 text-purple-800 text-xs font-bold rounded-full">
                                Formulario de 7 pasos y beneficios configurados
                            </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">
                            Expediente de oficina virtual: contratos, SIN/SEPREC, exhibición NIT, correspondencia y horas de regalo configuradas
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    <p className="text-sm text-[#61257b]">Adquiere y paga el servicio de oficina virtual en POS para habilitar el expediente.</p>
                </div>
            </div>

            {/* Tarjetas de Métricas */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-50 text-[#61257b] rounded-xl flex items-center justify-center font-bold">
                        <Building2 className="w-5 h-5" />
                    </div>
                    <div>
                        <div className="text-[11px] text-gray-500 font-bold uppercase">Total Empresas</div>
                        <div className="text-xl font-black text-gray-900">{stats.total}</div>
                    </div>
                </div>

                <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm flex items-center gap-3">
                    <div className="w-10 h-10 bg-emerald-50 text-emerald-600 rounded-xl flex items-center justify-center font-bold">
                        <CheckCircle2 className="w-5 h-5" />
                    </div>
                    <div>
                        <div className="text-[11px] text-gray-500 font-bold uppercase">Afiliados Activos</div>
                        <div className="text-xl font-black text-emerald-600">{stats.activos}</div>
                    </div>
                </div>

                <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-50 text-amber-600 rounded-xl flex items-center justify-center font-bold">
                        <Clock className="w-5 h-5" />
                    </div>
                    <div>
                        <div className="text-[11px] text-gray-500 font-bold uppercase">En Proceso (Miro)</div>
                        <div className="text-xl font-black text-amber-600">{stats.enProceso}</div>
                    </div>
                </div>

                <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-50 text-purple-600 rounded-xl flex items-center justify-center font-bold">
                        <Gift className="w-5 h-5" />
                    </div>
                    <div>
                        <div className="text-[11px] text-gray-500 font-bold uppercase">Horas Gratuitas Disp.</div>
                        <div className="text-xl font-black text-purple-600">{stats.totalHorasDisponibles} hrs</div>
                    </div>
                </div>
            </div>

            {/* Filtros y Búsqueda */}
            <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-3">
                <div className="relative w-full sm:w-80">
                    <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-3" />
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Buscar por empresa, NIT, cliente..."
                        className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs focus:ring-2 focus:ring-[#61257b] outline-none"
                    />
                </div>

                <div className="flex items-center gap-2 w-full sm:w-auto">
                    <Filter className="w-4 h-4 text-gray-400" />
                    <select
                        value={filterEstado}
                        onChange={(e) => setFilterEstado(e.target.value)}
                        className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs font-semibold text-gray-700 outline-none"
                    >
                        <option value="todos">Todos los Estados</option>
                        <option value="activo">Activos / Afiliados</option>
                        <option value="en_proceso">En Proceso</option>
                        <option value="vencido">Vencidos</option>
                    </select>
                </div>
            </div>

            {/* Listado / Tabla */}
            <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
                {loading ? (
                    <div className="p-12 text-center text-gray-400 text-xs flex flex-col items-center gap-2">
                        <Clock className="w-6 h-6 animate-spin text-[#61257b]" />
                        <span>Cargando afiliados...</span>
                    </div>
                ) : filteredList.length === 0 ? (
                    <div className="p-12 text-center text-gray-400 text-xs">
                        No se encontraron afiliaciones con los filtros seleccionados.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs">
                            <thead className="bg-gray-50/80 text-gray-500 font-bold border-b border-gray-100 uppercase tracking-wider text-[10px]">
                                <tr>
                                    <th className="px-6 py-4">Empresa / Razón Social</th>
                                    <th className="px-6 py-4">Cliente Titular</th>
                                    <th className="px-6 py-4">Progreso Flujo Miro</th>
                                    <th className="px-6 py-4">Plan & Cobranzas</th>
                                    <th className="px-6 py-4">Horas Gratuitas</th>
                                    <th className="px-6 py-4">Estado</th>
                                    <th className="px-6 py-4 text-right">Acciones</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {filteredList.map((item) => (
                                    <tr key={item.id} className="hover:bg-gray-50/50 transition">
                                        <td className="px-6 py-4">
                                            <div className="font-bold text-gray-900 text-sm">{item.empresa_nombre}</div>
                                            <div className="text-[11px] text-gray-400 flex items-center gap-2 mt-0.5">
                                                <span>NIT: {item.nit || 'Sin registrar'}</span>
                                                {item.rubro && <span>• {item.rubro}</span>}
                                            </div>
                                        </td>

                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-2">
                                                <span className="font-semibold text-gray-800">{item.cliente_nombre}</span>
                                                <span className="px-2 py-0.5 bg-blue-50 text-[#61257b] text-[10px] font-extrabold rounded-full border border-blue-100">
                                                    {item.cliente_rol || 'Cliente'}
                                                </span>
                                            </div>
                                            <div className="text-[11px] text-gray-400">{item.cliente_email}</div>
                                        </td>

                                        <td className="px-6 py-4">
                                            <div className="w-48 space-y-1.5">
                                                <div className="flex items-center justify-between text-[11px] font-bold">
                                                    <span className="text-gray-700">{item.pasos_completados_count}/7 Pasos</span>
                                                    <span className="text-gray-500 font-mono">{item.porcentaje_progreso}%</span>
                                                </div>
                                                <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                                                    <div
                                                        className={`h-full rounded-full transition-all duration-300 ${
                                                            item.pasos_completados_count >= 7
                                                                ? 'bg-emerald-500'
                                                                : 'bg-gradient-to-r from-[#c1348b] to-[#61257b]'
                                                        }`}
                                                        style={{ width: `${item.porcentaje_progreso}%` }}
                                                    />
                                                </div>
                                            </div>
                                        </td>

                                        <td className="px-6 py-4">
                                            <div className="flex flex-col gap-1">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-black text-gray-900 text-xs">
                                                        {Number(item.monto_total || 450).toFixed(2)} Bs
                                                    </span>
                                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase ${
                                                        item.estado_pago === 'pagado'
                                                            ? 'bg-emerald-100 text-emerald-800'
                                                            : item.estado_pago === 'parcial'
                                                            ? 'bg-amber-100 text-amber-800'
                                                            : 'bg-rose-100 text-rose-800'
                                                    }`}>
                                                        {item.estado_pago === 'pagado' ? 'Pagado' : item.estado_pago === 'parcial' ? 'Parcial' : 'Pendiente'}
                                                    </span>
                                                </div>
                                                <div className="text-[10px] text-gray-500">
                                                    {Number(item.saldo_pendiente || 0) > 0 ? (
                                                        <span className="text-rose-600 font-semibold">Saldo: {Number(item.saldo_pendiente).toFixed(2)} Bs</span>
                                                    ) : (
                                                        <span className="text-emerald-600 font-semibold">Al día</span>
                                                    )}
                                                </div>
                                            </div>
                                        </td>

                                        <td className="px-6 py-4">
                                            <div className="flex flex-col gap-1">
                                                <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-purple-50 text-purple-700 rounded-xl font-bold border border-purple-100 w-fit">
                                                    <Gift className="w-3.5 h-3.5 text-purple-600" />
                                                    <span>{item.horas_gratuitas_disponibles} hrs disp.</span>
                                                </div>
                                                <div className="text-[10px] text-gray-500 flex items-center gap-1">
                                                    <span className="font-semibold text-purple-800">
                                                        {item.periodo_inicio} a {item.periodo_fin}
                                                    </span>
                                                    <span>•</span>
                                                    <span>{item.horas_gratuitas_asignadas ?? 0}h/período</span>
                                                </div>
                                            </div>
                                        </td>

                                        <td className="px-6 py-4">
                                            <span
                                                className={`px-2.5 py-1 rounded-full font-bold text-[10px] uppercase tracking-wider ${
                                                    item.estado === 'activo'
                                                        ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                                                        : 'bg-amber-100 text-amber-800 border border-amber-200'
                                                }`}
                                            >
                                                {item.estado === 'activo' ? 'Activo / Afiliado' : 'En Proceso'}
                                            </span>
                                        </td>

                                        <td className="px-6 py-4 text-right">
                                            <div className="flex items-center justify-end gap-2">


                                                <button
                                                    onClick={() => setAfiliacionSeleccionada(item)}
                                                    className="aff-manage-flow" aria-label={`Gestionar flujo de ${item.empresa_nombre}`}
                                                >
                                                    <Sparkles size={16} aria-hidden="true" /><span>Gestionar flujo</span>
                                                    <ChevronRight className="w-3.5 h-3.5" />
                                                </button>

                                                <Link
                                                    to={`/kardex-citas/detalle/${item.cliente}`}
                                                    className="p-2 bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-900 rounded-xl transition"
                                                    title="Ver Kardex del Cliente"
                                                >
                                                    <ExternalLink className="w-4 h-4" />
                                                </Link>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modal para gestionar los 7 pasos de Miro */}
            {afiliacionSeleccionada && (
                <GestionAfiliacionModal
                    afiliacion={afiliacionSeleccionada}
                    onClose={() => setAfiliacionSeleccionada(null)}
                    onUpdated={(actualizada) => {
                        setAfiliaciones((prev) =>
                            prev.map((a) => (a.id === actualizada.id ? actualizada : a))
                        );
                        setAfiliacionSeleccionada(actualizada);
                    }}
                />
            )}

            {/* Modal para registrar pagos de domiciliación */}
            {afiliacionParaPago && (
                <PagoAfiliacionModal
                    isOpen={Boolean(afiliacionParaPago)}
                    onClose={() => setAfiliacionParaPago(null)}
                    afiliacion={afiliacionParaPago}
                    onPagoSuccess={() => {
                        loadData();
                    }}
                />
            )}

            {/* Modal para crear nueva afiliación */}
            {modalNuevaAbierto && (
                <NuevaAfiliacionModal
                    onClose={() => setModalNuevaAbierto(false)}
                    onCreated={(nueva) => {
                        setAfiliaciones((prev) => [nueva, ...prev]);
                    }}
                />
            )}
        </div>
    );
};

export default AfiliadosPage;


